import React, { useState, useEffect, useMemo, useRef } from 'react';
import { V } from '../../utils/theme';
import { Icons } from '../../components/Icons';
import { Card, Btn, Field, Chip, Skeleton, SuccessToastCtrl, ConfirmCtrl } from '../../components/ui';
import { Notice, Eyebrow, Callout } from '../../components/kit';
import { CenterModal } from '../../components/FullSheet';
import { fmtDay } from '../../utils/org';
import { hexToRgb } from '../../utils/brandTokens';
import { useApi, td, StatusPill, ToggleRow, STATUS_COLOR, SmallLabel, inputStyle } from './orgs/shared';
import { normalizeHex, APP_NAME_MAX, isSvgFile, downloadText, memberLabel, memberCounts, countsFromMembers, superAdminGate } from './orgs/helpers';
import { FlagsTab } from './orgs/FlagsTab';
import { MembersTab } from './orgs/MembersTab';
import { InvitesTab } from './orgs/InvitesTab';
import { AuditTab } from './orgs/AuditTab';

// ─── Super-admin: Organizations (white-label tenants) ───
// Tenant list · org detail (edit, branding, flags, members, invites, seats, audit,
// actions) · create org · seat report. The per-org tabs live in ./orgs/. Every
// org-scoped call carries `org_id` (SA_MAY_TARGET in the API).
const KINDS = ['gym', 'coach', 'trainer', 'other'];
const COLOR_FIELDS = [
  ['accent', 'Accent (dark)'], ['accent2', 'Accent 2 (dark)'], ['bg_dark', 'Background (dark)'],
  ['accent_light', 'Accent (light)'], ['accent2_light', 'Accent 2 (light)'], ['bg_light', 'Background (light)'],
];
const slugify = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
const thisMonth = () => new Date().toISOString().slice(0, 7);

// `isSuperAdmin` / `adminEmailConfigured` come from App.jsx's `/api/admin {action:'check'}`.
// `undefined` means "not yet known" — an older build, or the check has not answered — and
// behaves exactly as before: fire the call and let the API decide.
export function AdminOrgs({ isSuperAdmin, adminEmailConfigured }) {
  const [view, setView] = useState('list'); // list | detail | create | seats
  const [orgId, setOrgId] = useState(null);
  const gate = superAdminGate({ isSuperAdmin, adminEmailConfigured });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 20 }}>🏢</span>
        <div style={{ flex: 1, minWidth: 120 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: V.text }}>Organizations</div>
          <div style={{ fontSize: 10, color: V.text3 }}>Gyms & coaches · white-label tenants</div>
        </div>
        {!gate.blocked && (
          <div style={{ display: "flex", gap: 6 }}>
            <Chip label="Tenants" active={view === 'list' || view === 'detail'} onClick={() => setView('list')} />
            <Chip label="Seat report" active={view === 'seats'} onClick={() => setView('seats')} />
            <Chip label="+ New" active={view === 'create'} onClick={() => setView('create')} />
          </div>
        )}
      </div>
      {gate.blocked ? <SuperAdminOnly gate={gate} />
        : (<>
          {view === 'list' && <OrgList onOpen={(id) => { setOrgId(id); setView('detail'); }} adminEmailConfigured={adminEmailConfigured} />}
          {view === 'detail' && orgId && <OrgDetail orgId={orgId} onBack={() => setView('list')} />}
          {view === 'create' && <CreateOrg onCreated={(id) => { setOrgId(id); setView('detail'); }} />}
          {view === 'seats' && <SeatReport />}
        </>)}
    </div>
  );
}

// The gate's own screen. Shown before any sa_* call when `check` said no, and after
// one when the API answered super_admin_required anyway (an older App.jsx, or the
// signed-in address changed since the check).
function SuperAdminOnly({ gate }) {
  return (
    <div data-testid="org-super-admin-gate">
      <Card>
        <div style={{ fontSize: 15, fontWeight: 800, color: V.text, marginBottom: 6 }}>{gate.title}</div>
        <div style={{ fontSize: 12, color: V.text2, lineHeight: 1.5 }}>{gate.detail}</div>
        <div style={{ fontSize: 12, color: V.text3, lineHeight: 1.5, marginTop: 8 }}>{gate.cause}</div>
      </Card>
    </div>
  );
}

// ─── Tenant list ───
function OrgList({ onOpen, adminEmailConfigured }) {
  const api = useApi();
  const [orgs, setOrgs] = useState(null);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const [denied, setDenied] = useState(null);
  const load = async () => {
    setOrgs(null); setErr(''); setDenied(null);
    const r = await api('sa_list_orgs', { ...(status ? { status } : {}), ...(q.trim() ? { q: q.trim() } : {}) });
    if (r.ok) setOrgs(r.data.orgs || []);
    else {
      setOrgs([]);
      // A 403 from the API is the same answer the prop gives, so it gets the same
      // screen rather than the developer-facing "set ADMIN_EMAIL and redeploy" copy.
      // The refusal carries a `hint` naming the cause; it stands in when the prop has
      // not arrived (an App.jsx that does not pass adminEmailConfigured yet).
      const configured = adminEmailConfigured !== undefined ? adminEmailConfigured
        : r.err?.hint === 'ADMIN_EMAIL is not set on the API' ? false : undefined;
      const g = superAdminGate({ adminEmailConfigured: configured, errorCode: r.err?.code });
      if (g.blocked) setDenied(g); else setErr(r.msg);
    }
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  if (denied) return <SuperAdminOnly gate={denied} />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') load(); }} placeholder="Search name or slug"
          style={inputStyle({ flex: 1, minHeight: 40, borderRadius: 10, paddingLeft: 12, paddingRight: 12, fontSize: 14 })} />
        <Btn v="small" onClick={load}>Search</Btn>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {[['', 'All'], ['active', 'Active'], ['suspended', 'Suspended'], ['closed', 'Closed']].map(([v, l]) => <Chip key={v} label={l} active={status === v} onClick={() => setStatus(v)} color={STATUS_COLOR[v]} />)}
      </div>
      {err && <Notice tone="error">{err}</Notice>}
      {orgs === null ? <Card><Skeleton rows={4} /></Card>
        : orgs.length === 0 ? <Card><div style={{ fontSize: 12, color: V.text3, textAlign: "center" }}>No organizations{status ? ` with status ${status}` : ''}.</div></Card>
        : <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, color: V.text, minWidth: 640 }}>
            <thead><tr style={{ color: V.text3, fontSize: 10, textTransform: "uppercase", letterSpacing: ".06em" }}>
              {/* One number per meaning: "Clients" is the `member` role (what a seat limit
                  counts), "Staff" is owner + coaches, "Total" is every active membership. */}
              {['Name', 'Slug', 'Kind', 'Owner', 'Status', 'Clients', 'Staff', 'Total', 'Active (mo)', 'Created'].map(h => <th key={h} style={{ textAlign: "left", paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8, borderBottom: `1px solid ${V.cardBorder}` }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {orgs.map(o => {
                // sa_list_orgs sends members_active (role `member`) and coaches; the owner
                // row is a membership too, so Total adds it back rather than guessing.
                const c = memberCounts({ member: o.members_active, coach: o.coaches, owner: 1 });
                return (
                <tr key={o.id} onClick={() => onOpen(o.id)} style={{ cursor: "pointer" }} className="hover-lift" data-testid="org-row">
                  <td style={td}><b>{o.name}</b></td>
                  <td style={{ ...td, fontFamily: V.mono, color: V.text3 }}>{o.slug}</td>
                  <td style={td}>{o.kind}</td>
                  <td style={{ ...td, fontFamily: V.mono, fontSize: 11 }}>{o.owner_email}</td>
                  <td style={td}><StatusPill status={o.status} /></td>
                  <td style={{ ...td, fontFamily: V.mono }} data-testid="org-row-clients">{c.clients}{o.seat_limit ? ` / ${o.seat_limit}` : ''}</td>
                  <td style={{ ...td, fontFamily: V.mono }} data-testid="org-row-staff">{c.staff}</td>
                  <td style={{ ...td, fontFamily: V.mono, color: V.text3 }} data-testid="org-row-total">{c.total}</td>
                  <td style={{ ...td, fontFamily: V.mono }}>{o.clients_active_month ?? '—'}</td>
                  <td style={{ ...td, fontFamily: V.mono, color: V.text3 }}>{fmtDay(o.created_at)}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>}
    </div>
  );
}

// ─── Org detail ───
function OrgDetail({ orgId, onBack }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('overview'); // overview | branding | flags | members | invites | seats | audit
  const [suspendOpen, setSuspendOpen] = useState(false);
  const load = async () => {
    const r = await api('sa_org_detail', { org_id: orgId });
    if (r.ok) { setData(r.data); setErr(''); } else setErr(r.msg);
  };
  useEffect(() => { setData(null); load(); }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err && !data) return <Notice tone="error">{err}</Notice>;
  if (!data) return <Card><Skeleton rows={6} /></Card>;
  // Deliberately partial: sa_org_detail also returns `invites` and `audit`, and neither is
  // read here. The Invites tab gets live rows (with their redeemable `code` and `url`) from
  // `list_invites`, and the Audit tab pages `audit_log` — so the API no longer sends invite
  // codes on org-detail at all, and nothing on this screen depends on that field.
  const { org, branding, members = [], seats = [] } = data;

  const act = async (action, payload, okMsg) => {
    const r = await api(action, { org_id: orgId, ...payload });
    if (r.ok) { SuccessToastCtrl.show(okMsg); load(); } else setErr(r.msg);
    return r;
  };
  const reactivate = () => ConfirmCtrl.show(`Reactivate ${org.name}?`, 'Members get their branding and coach features back immediately.', () => act('sa_reactivate_org', {}, 'Reactivated'));
  const close = () => ConfirmCtrl.show(`Close ${org.name}?`, 'All memberships are removed, consent is revoked, invites are cancelled. This cannot be undone.',
    () => ConfirmCtrl.show('Really close this organization?', `Type-free double check: closing ${org.name} (${org.slug}) is permanent.`, () => act('sa_close_org', {}, 'Organization closed')));

  const active = members.filter(m => m.status === 'active');
  const byRole = { owner: [], coach: [], member: [] };
  for (const m of active) (byRole[m.role] || (byRole[m.role] = [])).push(m);
  // The one count everything on this screen reads: clients (role `member`) vs staff
  // (owner + coaches) vs total. The KPI, the tab chip, the Members tab and the flag
  // confirm all used to show a different number here.
  const counts = memberCounts(countsFromMembers(active));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="org-detail">
      <button onClick={onBack} style={{ background: "none", border: "none", display: "flex", alignItems: "center", gap: 5, cursor: "pointer", paddingTop: 4, paddingBottom: 4, paddingLeft: 0, paddingRight: 0, alignSelf: "flex-start" }}>
        {Icons.chevLeft({ size: 16, color: V.accent })}<span style={{ fontSize: 13, color: V.accent, fontWeight: 600 }}>All organizations</span>
      </button>
      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <BrandTile b={branding} size={52} />
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: V.text }}>{org.name} <StatusPill status={org.status} /></div>
            <div style={{ fontSize: 11, color: V.text3, fontFamily: V.mono }}>{org.slug} · {org.kind} · owner {org.owner_email}</div>
            {org.status === 'suspended' && org.suspended_reason && <div style={{ fontSize: 11, color: V.warn, marginTop: 2 }}>Suspended: {org.suspended_reason}</div>}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {org.status === 'active' && <Btn v="small" onClick={() => setSuspendOpen(true)} s={{ color: V.warn, background: `${V.warn}14`, border: `1px solid ${V.warn}30` }}>Suspend</Btn>}
            {org.status === 'suspended' && <Btn v="small" onClick={reactivate}>Reactivate</Btn>}
            {org.status !== 'closed' && <Btn v="small" onClick={close} s={{ color: V.danger, background: `${V.danger}14`, border: `1px solid ${V.danger}30` }}>Close</Btn>}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 8, marginTop: 12 }}>
          {[['Clients', `${counts.clients}${org.seat_limit ? ` / ${org.seat_limit}` : ''}`, 'org-kpi-clients'], ['Staff', `${counts.staff}`, 'org-kpi-staff'],
            ['Total members', `${counts.total}`, 'org-kpi-total'],
            ['Active clients (mo)', data.live_active_clients ?? '—'], ['Setup fee', org.setup_fee_paid_at ? 'Paid' : 'Unpaid'], ['Badge', org.badge_required ? 'Required' : 'Optional'], ['Created', fmtDay(org.created_at)]].map(([l, v, testId]) => (
            <div key={l} style={{ paddingTop: 8, paddingBottom: 8, paddingLeft: 8, paddingRight: 8, borderRadius: 10, background: V.inputBg }}>
              <div style={{ fontSize: 9, color: V.text3, textTransform: "uppercase", letterSpacing: ".06em" }}>{l}</div>
              <div data-testid={testId} style={{ fontSize: 14, fontWeight: 700, color: V.text, fontFamily: V.mono }}>{v}</div>
            </div>
          ))}
        </div>
      </Card>
      {err && <Notice tone="error">{err}</Notice>}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {/* The chip counts every active membership, and says so — it used to read
            `Members (N)` with N = everyone, next to a KPI where "Members" meant clients. */}
        {[['overview', 'Edit'], ['branding', 'Branding'], ['flags', 'Flags'], ['members', `Members (${counts.total} total)`], ['invites', 'Invites'], ['seats', 'Seats'], ['audit', 'Audit']].map(([k, l]) => <Chip key={k} label={l} active={tab === k} onClick={() => setTab(k)} />)}
      </div>
      {tab === 'overview' && <EditOrg org={org} members={active} onSaved={load} />}
      {tab === 'branding' && <BrandingForm orgId={orgId} org={org} branding={branding} onSaved={load} />}
      {tab === 'flags' && <FlagsTab orgId={orgId} orgName={org.name} clientCount={counts.clients} staffCount={counts.staff} />}
      {tab === 'members' && <MembersTab orgId={orgId} orgName={org.name} onChanged={load} />}
      {tab === 'invites' && <InvitesTab orgId={orgId} coaches={[...byRole.owner, ...byRole.coach]} />}
      {tab === 'seats' && <Seats seats={seats} />}
      {tab === 'audit' && <AuditTab orgId={orgId} orgSlug={org.slug} />}
      {suspendOpen && <SuspendModal org={org} onClose={() => setSuspendOpen(false)} onConfirm={async (reason) => { const r = await act('sa_suspend_org', { reason }, 'Suspended'); if (r.ok) setSuspendOpen(false); }} />}
    </div>
  );
}

function SuspendModal({ org, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  return (
    <CenterModal ariaLabel="Suspend organization" onBackdrop={onClose}>
      <div style={{ fontSize: 16, fontWeight: 800, color: V.text, marginBottom: 6 }}>Suspend {org.name}</div>
      <div style={{ fontSize: 12, color: V.text3, lineHeight: 1.5, marginBottom: 12 }}>Members see a "paused" screen and the app reverts to the IronLog look. Coach reads stop. No data is deleted. A reason is required and is shown to members.</div>
      <Field label="Reason" value={reason} onChange={setReason} placeholder="e.g. Invoice overdue 30 days" />
      <div style={{ display: "flex", gap: 8 }}>
        <Btn v="secondary" full onClick={onClose}>Cancel</Btn>
        <Btn full disabled={reason.trim().length < 3} onClick={() => onConfirm(reason.trim())} s={{ background: V.warn, color: '#08080d' }}>Suspend</Btn>
      </div>
    </CenterModal>
  );
}

// Edit org. Owner transfer picks from the active members (sa_org_detail rows carry
// `user_email` + `name`) and confirms naming both accounts before sa_update_org.
function EditOrg({ org, members, onSaved }) {
  const api = useApi();
  const [f, setF] = useState({ name: org.name || '', kind: org.kind || 'gym', seat_limit: org.seat_limit || '', setup_fee_paid: !!org.setup_fee_paid_at, billing_email: org.billing_email || '', billing_note: org.billing_note || '', badge_required: !!org.badge_required, owner_email: org.owner_email || '' });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF(x => ({ ...x, [k]: v }));
  const candidates = useMemo(() => {
    const list = (members || []).map(m => ({ email: m.user_email, name: m.name, role: m.role }));
    if (org.owner_email && !list.some(m => m.email === org.owner_email)) list.unshift({ email: org.owner_email, name: null, role: 'owner' });
    return list;
  }, [members, org.owner_email]);
  const transferring = f.owner_email && f.owner_email !== org.owner_email;
  const doSave = async () => {
    setBusy(true); setMsg(null);
    const payload = { org_id: org.id, name: f.name, kind: f.kind, seat_limit: f.seat_limit ? parseInt(f.seat_limit) : null, setup_fee_paid: f.setup_fee_paid, billing_email: f.billing_email, billing_note: f.billing_note, badge_required: f.badge_required };
    if (transferring) payload.owner_email = f.owner_email;
    const r = await api('sa_update_org', payload);
    if (r.ok) { setMsg({ ok: true, t: transferring ? 'Saved · ownership transferred' : 'Saved' }); onSaved(); } else setMsg({ ok: false, t: r.msg });
    setBusy(false);
  };
  const save = () => {
    if (!transferring) return doSave();
    const from = candidates.find(c => c.email === org.owner_email), to = candidates.find(c => c.email === f.owner_email);
    ConfirmCtrl.show(`Transfer ownership of ${org.name}?`, `From ${memberLabel(from || { email: org.owner_email })} to ${memberLabel(to || { email: f.owner_email })}. The previous owner becomes a coach; the new owner gets billing and every owner action.`, doSave);
  };
  return (
    <Card>
      <Eyebrow style={{ marginBottom: 10 }}>Organization</Eyebrow>
      <Field label="Name" value={f.name} onChange={set('name')} />
      <KindPicker value={f.kind} onChange={set('kind')} />
      <Field label="Seat limit (blank = unlimited)" value={f.seat_limit} onChange={set('seat_limit')} type="number" min={1} />
      <Field label="Billing email" value={f.billing_email} onChange={set('billing_email')} type="email" />
      <Field label="Billing note" value={f.billing_note} onChange={set('billing_note')} />
      <div style={{ marginBottom: 12 }}>
        <SmallLabel style={{ fontSize: 11, letterSpacing: ".08em", marginBottom: 6 }}>Owner (active member)</SmallLabel>
        <select value={f.owner_email} onChange={e => set('owner_email')(e.target.value)} aria-label="Owner" data-testid="owner-select" style={inputStyle({ width: "100%", minHeight: 44, borderRadius: 12, fontSize: 14, WebkitAppearance: "none" })}>
          {candidates.map(c => <option key={c.email} value={c.email}>{memberLabel(c)}{c.role === 'owner' ? ' — current owner' : ` — ${c.role}`}</option>)}
        </select>
        {transferring && <div style={{ fontSize: 10, color: V.warn, marginTop: 4 }}>Ownership moves from {org.owner_email} to {f.owner_email} on save.</div>}
      </div>
      <ToggleRow label="Setup fee paid" on={f.setup_fee_paid} onChange={set('setup_fee_paid')} />
      <ToggleRow label='"Powered by IronLog" badge required' on={f.badge_required} onChange={set('badge_required')} />
      {msg && <Notice tone={msg.ok ? 'ok' : 'error'} style={{ marginTop: 8 }}>{msg.t}</Notice>}
      <Btn full disabled={busy} onClick={save} s={{ marginTop: 12 }}>{busy ? 'Saving…' : transferring ? 'Save and transfer ownership' : 'Save changes'}</Btn>
    </Card>
  );
}

const KindPicker = ({ value, onChange }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 11, color: V.text3, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 6, fontWeight: 600 }}>Kind</div>
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{KINDS.map(k => <Chip key={k} label={k} active={value === k} onClick={() => onChange(k)} />)}</div>
  </div>
);

// ─── Branding (set_branding as sa with org_id) ───
function BrandTile({ b, size = 48 }) {
  const logo = b?.logo_small_data || b?.logo_url;
  return (
    <div style={{ width: size, height: size, borderRadius: size * 0.3, background: `linear-gradient(135deg,${b?.accent || '#00f5a0'},${b?.accent2 || '#00d9f5'})`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
      {logo ? <img src={logo} alt="" style={{ width: size, height: size, objectFit: "cover" }} /> : <span style={{ fontSize: size * 0.4, fontWeight: 800, color: b?.on_accent || '#08080d' }}>{(b?.app_name || '?')[0]}</span>}
    </div>
  );
}

// Rasterise an uploaded image to a square PNG data URL of `size` px (optionally on a solid fill)
function rasterize(img, size, fill) {
  const c = document.createElement('canvas'); c.width = size; c.height = size;
  const ctx = c.getContext('2d');
  if (fill) { ctx.fillStyle = fill; ctx.fillRect(0, 0, size, size); }
  const s = Math.min(img.naturalWidth, img.naturalHeight);
  const sx = (img.naturalWidth - s) / 2, sy = (img.naturalHeight - s) / 2;
  const pad = fill ? Math.round(size * 0.12) : 0; // Apple icon: inset logo on the solid background
  ctx.drawImage(img, sx, sy, s, s, pad, pad, size - pad * 2, size - pad * 2);
  return c.toDataURL('image/png');
}
const b64 = (dataUrl) => dataUrl.replace(/^data:[^,]+,/, '');
const revoke = (url) => { if (url) { try { URL.revokeObjectURL(url); } catch {} } };

function BrandingForm({ orgId, org, branding, onSaved }) {
  const api = useApi();
  const b = branding || {};
  const [f, setF] = useState({ app_name: (b.app_name || org.name || '').slice(0, APP_NAME_MAX), short_name: b.short_name || '', accent: b.accent || '#00f5a0', accent2: b.accent2 || '#00d9f5', bg_dark: b.bg_dark || '#08080d', bg_light: b.bg_light || '#f4f5f8', accent_light: b.accent_light || '#059669', accent2_light: b.accent2_light || '#0891b2', powered_by_badge: b.powered_by_badge ?? true });
  const [logo, setLogo] = useState(null); // { img, name, url }
  const [contrast, setContrast] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const logoUrlRef = useRef(null); // object URL of the current pick, revoked on re-pick / unmount
  useEffect(() => () => revoke(logoUrlRef.current), []);
  const set = (k) => (v) => { setF(x => ({ ...x, [k]: k === 'app_name' ? String(v).slice(0, APP_NAME_MAX) : v })); setContrast(null); };
  // Colours go to the API in 6-digit form (the API stores what it gets; #abc is expanded here)
  const colors = useMemo(() => Object.fromEntries(COLOR_FIELDS.map(([k]) => [k, normalizeHex(f[k]) || f[k]])), [f]);
  const validColors = COLOR_FIELDS.every(([k]) => hexToRgb(f[k]));
  const nameLen = f.app_name.trim().length;

  const pickLogo = (next) => { revoke(logoUrlRef.current); logoUrlRef.current = next?.url || null; setLogo(next); };
  const onFile = (file) => {
    if (!file) return;
    if (isSvgFile(file)) { setMsg({ ok: false, t: 'SVG logos are not accepted — upload a PNG, JPEG or WebP.' }); if (fileRef.current) fileRef.current.value = ''; return; }
    if (file.size > 15 * 1024 * 1024) { setMsg({ ok: false, t: 'Logo must be under 15 MB' }); return; } // resized to 512 px before upload
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { setMsg(null); pickLogo({ img, name: file.name, url }); };
    img.onerror = () => { setMsg({ ok: false, t: 'Could not read that image' }); revoke(url); };
    img.src = url;
  };

  const check = async () => {
    setBusy(true); setMsg(null);
    const r = await api('preview_contrast', { org_id: orgId, ...colors });
    if (r.ok) setContrast(r.data); else setMsg({ ok: false, t: r.msg });
    setBusy(false);
  };
  const applySuggested = () => { if (contrast?.suggested) { setF(x => ({ ...x, ...contrast.suggested })); setContrast(null); } };

  const save = async () => {
    setBusy(true); setMsg(null);
    const payload = { org_id: orgId, app_name: f.app_name.trim().slice(0, APP_NAME_MAX), short_name: (f.short_name || f.app_name).trim().slice(0, 12), ...colors, powered_by_badge: !!f.powered_by_badge };
    if (logo?.img) {
      try {
        const small = rasterize(logo.img, 96);
        if (small.length <= 20 * 1024 * 1.4) payload.logo_small_data = small; // ≤ 20 KB for the splash cache
        payload.logo_base64 = b64(rasterize(logo.img, 512));
        payload.icon_192_base64 = b64(rasterize(logo.img, 192));
        payload.icon_512_base64 = b64(rasterize(logo.img, 512));
        payload.apple_icon_base64 = b64(rasterize(logo.img, 180, colors.bg_dark)); // opaque, solid bg_dark
      } catch (e) { setMsg({ ok: false, t: 'Could not rasterize the logo' }); setBusy(false); return; }
    }
    const r = await api('set_branding', payload);
    if (r.ok) { setMsg({ ok: true, t: `Saved · branding v${r.data.version}` }); pickLogo(null); if (fileRef.current) fileRef.current.value = ''; onSaved(); }
    else if (r.err?.code === 'contrast_fail') { setContrast({ ok: false, pairs: r.err.pairs, suggested: r.err.suggested }); setMsg({ ok: false, t: 'Contrast check failed — see below' }); }
    else setMsg({ ok: false, t: r.msg });
    setBusy(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <Card>
        <Eyebrow style={{ marginBottom: 10 }}>Preview</Eyebrow>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {[['dark', f.bg_dark, f.accent, f.accent2, '#eeeef4'], ['light', f.bg_light, f.accent_light, f.accent2_light, '#16162a']].map(([mode, bg, a1, a2, text]) => (
            <div key={mode} style={{ borderRadius: 14, background: bg, border: `1px solid ${V.cardBorder}`, paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12, minHeight: 110 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: 9, background: `linear-gradient(135deg,${a1},${a2})`, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {(logo?.url || b.logo_small_data || b.logo_url) ? <img src={logo?.url || b.logo_small_data || b.logo_url} alt="" style={{ width: 28, height: 28, objectFit: "cover" }} /> : <span style={{ fontSize: 12, fontWeight: 800, color: b.on_accent || '#08080d' }}>{(f.app_name || '?')[0]}</span>}
                </div>
                <span style={{ fontSize: 14, fontWeight: 800, color: text, letterSpacing: "-0.02em" }}>{f.app_name || 'App name'}</span>
              </div>
              <div style={{ marginTop: 10, height: 30, borderRadius: 9, background: `linear-gradient(135deg,${a1},${a2})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: b.on_accent || (mode === 'dark' ? '#08080d' : '#fff') }}>Primary button</div>
              <div style={{ marginTop: 8, fontSize: 10, color: a1, fontWeight: 700 }}>{mode} mode · accent text</div>
            </div>
          ))}
        </div>
      </Card>
      <Card>
        <Eyebrow style={{ marginBottom: 10 }}>Branding</Eyebrow>
        <Field label={`App name (2–${APP_NAME_MAX}) · ${nameLen}/${APP_NAME_MAX}`} value={f.app_name} onChange={set('app_name')} />
        <Field label="Short name (home screen, ≤12)" value={f.short_name} onChange={(v) => set('short_name')(String(v).slice(0, 12))} placeholder={f.app_name.slice(0, 12)} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {COLOR_FIELDS.map(([k, l]) => (
            <div key={k} style={{ marginBottom: 8 }}>
              <SmallLabel>{l}</SmallLabel>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input type="color" value={normalizeHex(f[k]) || '#000000'} onChange={e => set(k)(e.target.value)} aria-label={l} style={{ width: 36, height: 36, border: "none", background: "none", padding: 0, cursor: "pointer" }} />
                <input value={f[k]} onChange={e => set(k)(e.target.value)} onBlur={() => { const n = normalizeHex(f[k]); if (n && n !== f[k]) set(k)(n); }} aria-label={`${l} hex`} style={inputStyle({ flex: 1, minWidth: 0, border: `1px solid ${hexToRgb(f[k]) ? V.cardBorder : V.danger}`, fontFamily: V.mono })} />
              </div>
            </div>
          ))}
        </div>
        <ToggleRow label='Show "Powered by IronLog" badge' hint={org.badge_required ? 'Locked on: badge is required for this org' : 'Optional for this org'} on={org.badge_required ? true : !!f.powered_by_badge} onChange={(v) => { if (!org.badge_required) set('powered_by_badge')(v); }} />
        <div style={{ marginTop: 8 }}>
          <SmallLabel style={{ marginBottom: 6 }}>Logo (PNG / JPEG / WebP, square, up to 15 MB — no SVG)</SmallLabel>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={e => onFile(e.target.files?.[0])} style={{ fontSize: 12, color: V.text2 }} />
          <div style={{ fontSize: 10, color: V.text3, marginTop: 4, lineHeight: 1.4 }}>Rasterised client-side to 96 px (splash cache), 192/512 (manifest) and 180 px on a solid dark background (Apple touch icon). Existing icons are kept when no file is chosen.</div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <Btn v="secondary" full disabled={busy || !validColors} onClick={check}>Check contrast</Btn>
          <Btn full disabled={busy || !validColors || nameLen < 2 || nameLen > APP_NAME_MAX} onClick={save}>{busy ? 'Saving…' : 'Save branding'}</Btn>
        </div>
        {msg && <Notice tone={msg.ok ? 'ok' : 'error'} style={{ marginTop: 8 }}>{msg.t}</Notice>}
      </Card>
      {contrast && (
        <Card glow={contrast.ok}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
            <Eyebrow>Contrast (WCAG 2.1)</Eyebrow>
            <span style={{ fontSize: 11, fontWeight: 700, color: contrast.ok ? V.accent : V.danger }}>{contrast.ok ? 'All pairs pass' : 'Some pairs fail'}</span>
          </div>
          {(contrast.pairs || []).map((p, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 6, paddingBottom: 6, borderTop: i ? `1px solid ${V.divider}` : 'none' }}>
              <span aria-hidden style={{ width: 8, height: 8, borderRadius: 4, background: p.ok ? V.accent : V.danger, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: V.text, flex: 1 }}>{p.name || p.pair || `Pair ${i + 1}`}</span>
              <span style={{ fontSize: 11, fontFamily: V.mono, color: p.ok ? V.text2 : V.danger }}>{p.ratio != null ? `${Number(p.ratio).toFixed(2)}:1` : ''}{p.min != null ? ` (min ${p.min})` : ''}</span>
            </div>
          ))}
          {!contrast.ok && contrast.suggested && (
            <div style={{ marginTop: 10 }}>
              <Callout icon={Icons.zap} title="Suggested fix">Nudges lightness until every pair passes. Changed: {(contrast.suggested_changed || Object.keys(contrast.suggested)).join(', ') || 'none'}.</Callout>
              <Btn full onClick={applySuggested} s={{ marginTop: 8 }}>Fix automatically</Btn>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// ─── Seats ───
function Seats({ seats }) {
  const max = Math.max(1, ...seats.map(s => s.active_clients || 0));
  return (
    <Card>
      <Eyebrow style={{ marginBottom: 8 }}>Seat snapshots (last 12 months)</Eyebrow>
      {seats.length === 0 ? <div style={{ fontSize: 12, color: V.text3 }}>No snapshots yet — run "Snapshot now" from the Seat report.</div> : seats.map(s => (
        <div key={s.month} style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 4, paddingBottom: 4 }}>
          <span style={{ fontFamily: V.mono, fontSize: 11, color: V.text2, width: 62 }}>{String(s.month).slice(0, 7)}</span>
          <div style={{ flex: 1, height: 10, borderRadius: 5, background: V.inputBg, overflow: "hidden" }}><div style={{ width: `${Math.round((s.active_clients || 0) / max * 100)}%`, height: "100%", background: `linear-gradient(90deg,${V.accent},${V.accent2})` }} /></div>
          <span style={{ fontFamily: V.mono, fontSize: 12, fontWeight: 700, color: V.text, width: 36, textAlign: "right" }}>{s.active_clients}</span>
          <span style={{ fontSize: 10, color: V.text3, width: 60 }}>{s.member_rows} rows</span>
        </div>
      ))}
    </Card>
  );
}

// ─── Create org ───
function CreateOrg({ onCreated }) {
  const api = useApi();
  const [f, setF] = useState({ name: '', slug: '', kind: 'gym', owner_email: '', seat_limit: '', setup_fee_paid: false, billing_email: '', badge_required: true });
  const [slugTouched, setSlugTouched] = useState(false);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF(x => ({ ...x, [k]: v, ...(k === 'name' && !slugTouched ? { slug: slugify(v) } : {}) }));
  const create = async () => {
    setBusy(true); setMsg(null);
    const r = await api('sa_create_org', { name: f.name.trim(), slug: f.slug || slugify(f.name), kind: f.kind, owner_email: f.owner_email.trim(), seat_limit: f.seat_limit ? parseInt(f.seat_limit) : null, setup_fee_paid: f.setup_fee_paid, billing_email: f.billing_email.trim() || null, badge_required: f.badge_required });
    if (r.ok) { SuccessToastCtrl.show(`Created ${r.data.org.name}`); onCreated(r.data.org.id); }
    else setMsg({ owner_not_found: 'No verified user with that email — the owner must sign up first.', slug_taken: 'That slug is taken.', owner_already_in_org: 'That user is already in an organization.' }[r.err?.code] || r.msg);
    setBusy(false);
  };
  const ready = f.name.trim().length >= 2 && /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/.test(f.slug) && /.+@.+\..+/.test(f.owner_email);
  return (
    <Card>
      <Eyebrow style={{ marginBottom: 10 }}>New organization</Eyebrow>
      <Field label="Name" value={f.name} onChange={set('name')} placeholder="Iron Temple Gym" autoFocus />
      <Field label="Slug (auto from name)" value={f.slug} onChange={(v) => { setSlugTouched(true); setF(x => ({ ...x, slug: slugify(v) })); }} />
      <KindPicker value={f.kind} onChange={set('kind')} />
      <Field label="Owner email (existing verified user)" value={f.owner_email} onChange={set('owner_email')} type="email" />
      <Field label="Seat limit (blank = unlimited)" value={f.seat_limit} onChange={set('seat_limit')} type="number" min={1} />
      <Field label="Billing email (optional)" value={f.billing_email} onChange={set('billing_email')} type="email" />
      <ToggleRow label="Setup fee paid" on={f.setup_fee_paid} onChange={set('setup_fee_paid')} />
      <ToggleRow label='"Powered by IronLog" badge required' on={f.badge_required} onChange={set('badge_required')} />
      {msg && <Notice tone="error" style={{ marginTop: 6 }}>{msg}</Notice>}
      <Btn full disabled={busy || !ready} onClick={create} s={{ marginTop: 12 }}>{busy ? 'Creating…' : 'Create organization'}</Btn>
    </Card>
  );
}

// ─── Seat report ───
// The month picker reloads on change (the old "Load" button was the only trigger);
// the CSV is saved through a Blob rather than a data: URL.
function SeatReport() {
  const api = useApi();
  const [month, setMonth] = useState(thisMonth());
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const load = async (m) => {
    setRep(null); setErr('');
    const r = await api('sa_seat_report', { month: m });
    if (r.ok) setRep(r.data); else setErr(r.msg);
  };
  useEffect(() => { if (/^\d{4}-\d{2}$/.test(month)) load(month); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  const snapshot = async () => {
    setBusy(true);
    const r = await api('sa_snapshot_seats', { month });
    if (r.ok) { SuccessToastCtrl.show(`Snapshot taken: ${r.data.taken ?? 0} org(s)`); load(month); } else setErr(r.msg);
    setBusy(false);
  };
  const download = () => { if (rep?.csv && !downloadText(`ironlog-seats-${rep.month}.csv`, rep.csv)) setErr('Download is not available in this browser.'); };
  const total = (rep?.rows || []).reduce((n, r) => n + (r.active_clients || 0), 0);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="seat-report">
      <Card>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 140 }}><Field label="Month" value={month} onChange={setMonth} type="month" /></div>
          <Btn v="small" onClick={() => load(month)} s={{ marginBottom: 12 }}>Reload</Btn>
          <Btn v="small" disabled={busy} onClick={snapshot} s={{ marginBottom: 12 }}>{busy ? 'Snapshotting…' : 'Snapshot now'}</Btn>
          <Btn v="small" disabled={!rep?.csv} onClick={download} s={{ marginBottom: 12 }}>Download CSV</Btn>
        </div>
        {rep && <div style={{ fontSize: 11, color: V.text3 }}>{rep.month} · {rep.frozen ? 'frozen snapshot' : 'live (current month, not frozen)'} · {total} active clients across {rep.rows.length} org(s)</div>}
      </Card>
      {err && <Notice tone="error">{err}</Notice>}
      {rep === null && !err ? <Card><Skeleton rows={4} /></Card> : rep && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, color: V.text, minWidth: 520 }}>
            <thead><tr style={{ color: V.text3, fontSize: 10, textTransform: "uppercase", letterSpacing: ".06em" }}>
              {['Org', 'Slug', 'Status', 'Active clients', 'Member rows', 'Frozen', 'Taken'].map(h => <th key={h} style={{ textAlign: "left", paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8, borderBottom: `1px solid ${V.cardBorder}` }}>{h}</th>)}
            </tr></thead>
            <tbody>{rep.rows.map(r => (
              <tr key={r.org_id}>
                <td style={td}><b>{r.name}</b></td><td style={{ ...td, fontFamily: V.mono, color: V.text3 }}>{r.slug}</td><td style={td}><StatusPill status={r.status} /></td>
                <td style={{ ...td, fontFamily: V.mono, fontWeight: 700 }}>{r.active_clients ?? '—'}</td><td style={{ ...td, fontFamily: V.mono }}>{r.member_rows ?? '—'}</td>
                <td style={td}>{r.frozen ? 'yes' : 'no'}</td><td style={{ ...td, fontFamily: V.mono, color: V.text3 }}>{r.taken_at ? fmtDay(r.taken_at) : '—'}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
